/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useMemo } from 'react';
import { ActivityIndicator, StyleSheet, Text, TouchableOpacity, View, useWindowDimensions } from 'react-native';
import { spacing, ticketColors, ticketFonts, typography } from '../../shared/theme/public-api';
import { operationalFilterStyles as opChipStyles } from '../filters/operationalFilters.styles';

const MAX_CATALOG_ITEMS_IN_MEMORY = 200;
const DEFAULT_LIST_NOUNS = { singular: 'producto', plural: 'productos' };

export type CatalogPaginationPlacement = 'footer' | 'top';

export type CatalogPageSizePickerConfig<O extends readonly number[] = readonly number[]> = {
  /** Etiqueta junto a las chips (p. ej. «Ítems por página», «Clientes por página»). */
  label?: string;
  options: O;
  value: O[number];
  onChange: (size: O[number]) => void;
  accessibilityLabelPrefix?: string;
  /** Etiqueta visible en cada chip (p. ej. «2 filas» en grilla POS). */
  formatOptionLabel?: (size: O[number]) => string;
};

/** Configuración típica de paginación operativa (5 o 10 ítems por página). */
export type OperationalPageSizePickerConfig = CatalogPageSizePickerConfig<readonly [5, 10]>;

type CatalogPaginationProps = {
  catalogPageIndex: number;
  pageSize: number;
  catalogTotalCount: number | null;
  hasMore: boolean;
  productsLoading: boolean;
  productCount: number;
  onPreviousPage: () => void;
  onNextPage: () => void;
  placement?: CatalogPaginationPlacement;
  listNouns?: { singular: string; plural: string };
  alwaysShowPagination?: boolean;
  variant?: 'default' | 'infinite';
  productsLoadingMore?: boolean;
  catalogAtCap?: boolean;
  criteriaRefreshing?: boolean;
  footerStripTitle?: string;
  pageSizePicker?: OperationalPageSizePickerConfig | CatalogPageSizePickerConfig;
  /** Paginación sin total global (Libro de ventas). */
  cursorPagination?: boolean;
};

const DEFAULT_PAGE_SIZE_LABEL = 'Ítems por página';

function CatalogPageSizePicker({
  config,
  compact,
}: {
  config: OperationalPageSizePickerConfig | CatalogPageSizePickerConfig;
  compact: boolean;
}) {
  const label = config.label ?? DEFAULT_PAGE_SIZE_LABEL;
  const a11yPrefix = config.accessibilityLabelPrefix ?? label;

  return (
    <View style={[styles.catalogPagerPageSizeGroup, compact && styles.catalogPagerPageSizeGroupCompact]}>
      <Text style={styles.catalogPagerPageSizeLabel}>{label}</Text>
      <View style={styles.catalogPagerPageSizeChips}>
        {config.options.map((n) => {
          const selected = config.value === n;
          const chipLabel = config.formatOptionLabel
            ? (config.formatOptionLabel as (size: number) => string)(n)
            : String(n);
          return (
            <TouchableOpacity
              key={n}
              style={[
                opChipStyles.chip,
                selected && opChipStyles.chipActive,
                styles.catalogPagerPageSizeChip,
                config.formatOptionLabel && styles.catalogPagerPageSizeChipRows,
              ]}
              onPress={() => (config.onChange as (size: number) => void)(n)}
              accessibilityRole="button"
              accessibilityState={{ selected }}
              accessibilityLabel={`${a11yPrefix}: ${chipLabel}`}
            >
              <Text style={[opChipStyles.chipText, selected && opChipStyles.chipTextActive]}>{chipLabel}</Text>
            </TouchableOpacity>
          );
        })}
      </View>
    </View>
  );
}

export const CatalogPagination = ({
  catalogPageIndex,
  pageSize,
  catalogTotalCount,
  hasMore,
  productsLoading,
  productCount,
  onPreviousPage,
  onNextPage,
  placement = 'footer',
  listNouns = DEFAULT_LIST_NOUNS,
  alwaysShowPagination = false,
  variant = 'default',
  productsLoadingMore = false,
  catalogAtCap = false,
  criteriaRefreshing = false,
  footerStripTitle,
  pageSizePicker,
  cursorPagination = false,
}: CatalogPaginationProps) => {
  const { width } = useWindowDimensions();
  const compactFooterStrip = width < 520;
  const nSing = listNouns.singular;
  const nPlur = listNouns.plural;

  if (variant === 'infinite') {
    const loadedSummary =
      criteriaRefreshing
        ? `Actualizando ${nPlur}…`
        : catalogTotalCount === null
          ? `Mostrando ${productCount} ${productCount === 1 ? nSing : nPlur}`
          : catalogTotalCount === 0
            ? `0 ${nPlur} con este criterio`
            : `Mostrando 1-${productCount} de ${catalogTotalCount}`;
    const topSummary =
      catalogTotalCount === null
        ? `${loadedSummary} · total…`
        : catalogTotalCount === 0
          ? loadedSummary
          : `${loadedSummary} ${nPlur}`;

    if (placement === 'top') {
      if (productCount === 0 && !productsLoading) return null;
      return (
        <View style={styles.catalogPagerTopWrap} accessibilityLabel={topSummary}>
          <Text style={styles.catalogPagerTopSummary} numberOfLines={1}>
            {topSummary}
          </Text>
        </View>
      );
    }

    if (productCount === 0 && !productsLoading) return null;

    return (
      <View style={styles.catalogPagerWrap}>
        <View style={styles.catalogPagerCanvasRow} accessibilityLabel={topSummary}>
          <Text style={styles.catalogPagerTopSummary} numberOfLines={1}>
            {loadedSummary}
          </Text>
          <View style={styles.catalogPagerCanvasActions}>
            <TouchableOpacity
              style={[styles.catalogPagerButton, styles.catalogPagerButtonTop, styles.catalogPagerButtonDisabled]}
              onPress={onPreviousPage}
              disabled
              accessibilityLabel="Página anterior del catálogo"
            >
              <Text style={styles.catalogPagerButtonText}>Anterior</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[
                styles.catalogPagerButton,
                styles.catalogPagerButtonTop,
                (!hasMore || productsLoading || productsLoadingMore) && styles.catalogPagerButtonDisabled,
              ]}
              onPress={onNextPage}
              disabled={!hasMore || productsLoading || productsLoadingMore}
              accessibilityLabel="Cargar más resultados"
            >
              <Text style={styles.catalogPagerButtonText}>{productsLoadingMore ? 'Cargando…' : 'Siguiente'}</Text>
            </TouchableOpacity>
          </View>
        </View>
        {productsLoadingMore && productCount > 0 ? (
          <View style={styles.loadingBox}>
            <ActivityIndicator color={ticketColors.ink2} />
          </View>
        ) : null}
        {catalogAtCap && catalogTotalCount != null && catalogTotalCount > productCount ? (
          <Text style={styles.caption}>
            Se muestran los primeros {MAX_CATALOG_ITEMS_IN_MEMORY} resultados. Afiná la búsqueda o el filtro para
            acotar.
          </Text>
        ) : null}
      </View>
    );
  }

  const pageNum = catalogPageIndex + 1;
  const { totalPages, hideWhenSingleFullPage } = useMemo(() => {
    if (cursorPagination) {
      return { totalPages: null as number | null, hideWhenSingleFullPage: false };
    }
    if (catalogTotalCount === null) return { totalPages: null as number | null, hideWhenSingleFullPage: false };
    const pages = Math.max(1, Math.ceil(catalogTotalCount / pageSize));
    const hide = !alwaysShowPagination && catalogTotalCount > 0 && pages <= 1 && catalogPageIndex === 0;
    return { totalPages: pages, hideWhenSingleFullPage: hide };
  }, [catalogTotalCount, pageSize, catalogPageIndex, alwaysShowPagination, cursorPagination]);

  const paginationSummary = useMemo(() => {
    if (criteriaRefreshing && productCount > 0) {
      return `Actualizando ${nPlur}…`;
    }
    if (cursorPagination) {
      if (productCount > 0) {
        const noun = productCount === 1 ? nSing : nPlur;
        return `Página ${pageNum} · ${productCount} ${noun}`;
      }
      return `Página ${pageNum}`;
    }
    if (catalogTotalCount === null) return `Página ${pageNum} · obteniendo total…`;
    if (catalogTotalCount === 0) return `0 ${nPlur} con este criterio`;
    const pages = totalPages ?? Math.max(1, Math.ceil(catalogTotalCount / pageSize));
    const noun = catalogTotalCount === 1 ? nSing : nPlur;
    return `Página ${pageNum} de ${pages} · ${catalogTotalCount} ${noun} en total`;
  }, [
    catalogTotalCount,
    pageNum,
    pageSize,
    totalPages,
    nSing,
    nPlur,
    cursorPagination,
    productCount,
    catalogPageIndex,
    criteriaRefreshing,
  ]);

  const compactTopSummary = useMemo(() => {
    if (cursorPagination) return `Pág. ${pageNum}`;
    if (catalogTotalCount === null) return `Pág. ${pageNum} · …`;
    if (catalogTotalCount === 0) return `0 ${nPlur}`;
    if (productCount === 0) return `Pág. ${pageNum}`;
    const from = catalogPageIndex * pageSize + 1;
    const to = Math.min(catalogTotalCount, catalogPageIndex * pageSize + productCount);
    return `${from}-${to} de ${catalogTotalCount}`;
  }, [catalogTotalCount, catalogPageIndex, pageSize, productCount, pageNum, nPlur, cursorPagination]);

  const showPager = catalogPageIndex > 0 || productCount > 0;
  const showPaginationBlock = cursorPagination
    ? alwaysShowPagination || productCount > 0 || catalogPageIndex > 0
    : !hideWhenSingleFullPage && (productCount > 0 || catalogTotalCount !== null);
  const showFooterStrip = Boolean(pageSizePicker) || showPaginationBlock;
  const prevDisabled = catalogPageIndex === 0 || productsLoading;
  const nextDisabled = !hasMore || productsLoading;

  if (placement === 'top') {
    if (!showPaginationBlock || !showPager) return null;
    return (
      <View style={styles.catalogPagerTopWrap}>
        <View style={styles.catalogPagerTopRow}>
          <TouchableOpacity
            style={[styles.catalogPagerButton, styles.catalogPagerButtonTop, prevDisabled && styles.catalogPagerButtonDisabled]}
            onPress={onPreviousPage}
            disabled={prevDisabled}
            accessibilityLabel="Página anterior"
          >
            <Text style={styles.catalogPagerButtonText}>Anterior</Text>
          </TouchableOpacity>
          <Text style={styles.catalogPagerTopSummary} numberOfLines={1}>
            {compactTopSummary}
          </Text>
          <TouchableOpacity
            style={[styles.catalogPagerButton, styles.catalogPagerButtonTop, nextDisabled && styles.catalogPagerButtonDisabled]}
            onPress={onNextPage}
            disabled={nextDisabled}
            accessibilityLabel="Página siguiente"
          >
            <Text style={styles.catalogPagerButtonText}>Siguiente</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  const pagerActions = (
    <View style={styles.catalogPagerCanvasActions}>
      <TouchableOpacity
        style={[styles.catalogPagerButton, styles.catalogPagerButtonTop, prevDisabled && styles.catalogPagerButtonDisabled]}
        onPress={onPreviousPage}
        disabled={prevDisabled}
      >
        <Text style={styles.catalogPagerButtonText}>Anterior</Text>
      </TouchableOpacity>
      <TouchableOpacity
        style={[styles.catalogPagerButton, styles.catalogPagerButtonTop, nextDisabled && styles.catalogPagerButtonDisabled]}
        onPress={onNextPage}
        disabled={nextDisabled}
      >
        <Text style={styles.catalogPagerButtonText}>Siguiente</Text>
      </TouchableOpacity>
    </View>
  );

  if (footerStripTitle) {
    return (
      <>
        {productsLoading && productCount > 0 ? (
          <View style={styles.loadingBox}>
            <ActivityIndicator color={ticketColors.ink2} />
            <Text style={styles.caption}>Cargando página…</Text>
          </View>
        ) : null}
        {showPaginationBlock ? (
          <View style={styles.catalogPagerCanvasStrip}>
            {compactFooterStrip ? (
              <>
                <Text style={styles.catalogPagerCanvasStripLabel}>{footerStripTitle}</Text>
                <Text style={[styles.catalogPagerCanvasStripCenter, styles.catalogPagerCanvasStripCenterCompact]} numberOfLines={2}>
                  {paginationSummary}
                </Text>
                {showPager ? pagerActions : null}
              </>
            ) : (
              <View style={styles.catalogPagerCanvasStripRowWide}>
                <Text style={styles.catalogPagerCanvasStripLabel}>{footerStripTitle}</Text>
                <Text style={styles.catalogPagerCanvasStripCenter} numberOfLines={1}>
                  {paginationSummary}
                </Text>
                {showPager ? pagerActions : null}
              </View>
            )}
          </View>
        ) : null}
      </>
    );
  }

  if (pageSizePicker) {
    return (
      <>
        {productsLoading && productCount > 0 ? (
          <View style={styles.loadingBox}>
            <ActivityIndicator color={ticketColors.ink2} />
            <Text style={styles.caption}>Cargando página…</Text>
          </View>
        ) : null}
        {showFooterStrip ? (
          <View style={styles.catalogPagerUnifiedStrip}>
            {compactFooterStrip ? (
              <>
                <CatalogPageSizePicker config={pageSizePicker} compact />
                {showPaginationBlock ? (
                  <>
                    <Text style={styles.catalogPagerUnifiedSummary} numberOfLines={2}>
                      {paginationSummary}
                    </Text>
                    {showPager ? pagerActions : null}
                  </>
                ) : null}
              </>
            ) : (
              <View style={styles.catalogPagerUnifiedStripRowWide}>
                <CatalogPageSizePicker config={pageSizePicker} compact={false} />
                {showPaginationBlock ? (
                  <>
                    <Text style={styles.catalogPagerUnifiedSummary} numberOfLines={1}>
                      {paginationSummary}
                    </Text>
                    {showPager ? pagerActions : null}
                  </>
                ) : null}
              </View>
            )}
          </View>
        ) : null}
      </>
    );
  }

  return (
    <>
      {productsLoading && productCount > 0 ? (
        <View style={styles.loadingBox}>
          <ActivityIndicator color={ticketColors.ink2} />
          <Text style={styles.caption}>Cargando página…</Text>
        </View>
      ) : null}
      {showPaginationBlock ? (
        <View style={styles.catalogPagerWrap}>
          <Text style={styles.catalogPagerHint}>{paginationSummary}</Text>
          {showPager ? (
            <View style={styles.catalogPager}>
              <TouchableOpacity
                style={[styles.catalogPagerButton, catalogPageIndex === 0 && styles.catalogPagerButtonDisabled]}
                onPress={onPreviousPage}
                disabled={prevDisabled}
              >
                <Text style={styles.catalogPagerButtonText}>Anterior</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.catalogPagerButton, nextDisabled && styles.catalogPagerButtonDisabled]}
                onPress={onNextPage}
                disabled={nextDisabled}
              >
                <Text style={styles.catalogPagerButtonText}>Siguiente</Text>
              </TouchableOpacity>
            </View>
          ) : null}
        </View>
      ) : null}
    </>
  );
};

const styles = StyleSheet.create({
  loadingBox: {
    paddingVertical: spacing.sm,
    alignItems: 'center',
    gap: spacing.xs,
  },
  caption: {
    ...typography.caption,
    color: ticketColors.ink2,
    marginTop: spacing.xs,
  },
  catalogPagerTopWrap: {
    marginTop: spacing.xs,
    marginBottom: spacing.xs,
  },
  catalogPagerTopRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  catalogPagerTopSummary: {
    flex: 1,
    ...typography.caption,
    color: ticketColors.ink2,
    textAlign: 'center',
  },
  catalogPagerWrap: {
    marginTop: spacing.sm,
    gap: spacing.xs,
  },
  catalogPagerCanvasRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  catalogPagerCanvasActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  catalogPagerButton: {
    minHeight: 34,
    paddingHorizontal: spacing.md,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
    alignItems: 'center',
    justifyContent: 'center',
  },
  catalogPagerButtonTop: {
    minWidth: 96,
  },
  catalogPagerButtonDisabled: {
    opacity: 0.45,
  },
  catalogPagerButtonText: {
    ...typography.caption,
    color: ticketColors.ink,
    fontWeight: '700',
  },
  catalogPagerHint: {
    ...typography.caption,
    color: ticketColors.ink2,
  },
  catalogPager: {
    flexDirection: 'row',
    gap: spacing.xs,
  },
  catalogPagerCanvasStrip: {
    marginTop: spacing.xs,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 10,
    backgroundColor: ticketColors.paper2,
    padding: spacing.sm,
  },
  catalogPagerCanvasStripRowWide: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  catalogPagerCanvasStripLabel: {
    ...typography.caption,
    color: ticketColors.ink3,
    fontWeight: '700',
  },
  catalogPagerCanvasStripCenter: {
    flex: 1,
    ...typography.caption,
    color: ticketColors.ink2,
  },
  catalogPagerCanvasStripCenterCompact: {
    marginTop: spacing.xs,
    marginBottom: spacing.xs,
  },
  catalogPagerUnifiedStrip: {
    width: '100%',
    maxWidth: '100%',
    alignSelf: 'stretch',
    marginTop: spacing.sm,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 10,
    backgroundColor: ticketColors.paper2,
    padding: spacing.sm,
    gap: spacing.sm,
  },
  catalogPagerUnifiedStripRowWide: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  catalogPagerUnifiedSummary: {
    flex: 1,
    minWidth: 0,
    ...typography.caption,
    color: ticketColors.ink2,
  },
  catalogPagerPageSizeGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    flexShrink: 0,
  },
  catalogPagerPageSizeGroupCompact: {
    flexWrap: 'wrap',
  },
  catalogPagerPageSizeLabel: {
    ...typography.caption,
    color: ticketColors.ink2,
    fontWeight: '700',
  },
  catalogPagerPageSizeChips: {
    flexDirection: 'row',
    gap: spacing.xs,
  },
  catalogPagerPageSizeChip: {
    minWidth: 44,
    alignItems: 'center',
  },
  catalogPagerPageSizeChipRows: {
    minWidth: 72,
  },
});
